/**
 * React Hook for Azure AD Authentication
 * Uses MSAL.js (Microsoft Authentication Library) for authentication
 *
 * Authentication is deferred until explicitly requested via login()
 * Silent auth is attempted on mount for returning users (no popup)
 */

import { useState, useEffect, useCallback } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { getMsalConfig, getTokenRequest } from '../config/constants';

// Check if running in demo/local mode
const isDemoMode = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

// Create MSAL instance (singleton)
let msalInstance = null;
let msalInitialized = false;

async function getMsalInstance() {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(getMsalConfig());
  }
  if (!msalInitialized) {
    await msalInstance.initialize();
    msalInitialized = true;
  }
  return msalInstance;
}

export function useAuth() {
  const [accessToken, setAccessToken] = useState(isDemoMode() ? 'demo-token' : null);
  const [account, setAccount] = useState(isDemoMode() ? { username: 'demo@example.com', name: 'Demo User' } : null);
  const [loading, setLoading] = useState(false); // Start false - no blocking on auth
  const [error, setError] = useState(null);

  /**
   * Try to get token silently on mount (cached/SSO) - no popup
   * This runs in background without blocking UI
   */
  useEffect(() => {
    // Skip real auth in demo mode
    if (isDemoMode()) {
      console.log('[Pod] useAuth: DEMO MODE - using mock authentication');
      return;
    }
    console.log('[Pod] useAuth: Attempting silent auth...');
    trySilentAuth();
  }, []);

  /**
   * Attempt silent token acquisition (no popup)
   * For returning users who have cached credentials
   */
  async function trySilentAuth() {
    try {
      const msal = await getMsalInstance();

      // Try to get existing accounts
      const accounts = msal.getAllAccounts();

      if (accounts.length > 0) {
        console.log('[Pod] Found existing account:', accounts[0].username);
        setAccount(accounts[0]);

        // Try silent token acquisition
        try {
          const tokenResponse = await msal.acquireTokenSilent({
            ...getTokenRequest(),
            account: accounts[0]
          });

          console.log('[Pod] Access token acquired silently');
          setAccessToken(tokenResponse.accessToken);
        } catch (silentError) {
          // Silent acquisition failed - user will need to click login
          console.log('[Pod] Silent token acquisition failed, user will need to login:', silentError.message);
          // Don't set error - this is expected for first-time users
        }
      } else {
        console.log('[Pod] No existing accounts found, user will need to login');
      }
    } catch (err) {
      console.log('[Pod] MSAL initialization error:', err.message);
      // Don't set error - silent failures are expected
    }
  }

  /**
   * Explicit login - triggers authentication popup
   * Called when user clicks "Analyze Ticket" or other actions requiring auth
   * @returns {Promise<string>} The access token
   */
  const login = useCallback(async () => {
    // Demo mode - return mock token immediately
    if (isDemoMode()) {
      console.log('[Pod] DEMO MODE - returning mock token');
      return 'demo-token';
    }

    // If already have token, return it
    if (accessToken) {
      console.log('[Pod] Already authenticated, returning existing token');
      return accessToken;
    }

    try {
      setLoading(true);
      setError(null);

      const msal = await getMsalInstance();
      const accounts = msal.getAllAccounts();

      // First try silent acquisition (in case token was refreshed)
      if (accounts.length > 0) {
        try {
          const tokenResponse = await msal.acquireTokenSilent({
            ...getTokenRequest(),
            account: accounts[0]
          });

          console.log('[Pod] Access token acquired silently during login');
          setAccessToken(tokenResponse.accessToken);
          setAccount(accounts[0]);
          setLoading(false);
          return tokenResponse.accessToken;
        } catch {
          console.log('[Pod] Silent acquisition failed, falling back to popup');
          // Fall through to popup
        }
      }

      // Popup login required
      console.log('[Pod] Initiating login popup...');
      const loginResponse = await msal.loginPopup(getTokenRequest());

      console.log('[Pod] Login successful via popup');
      setAccount(loginResponse.account);
      setAccessToken(loginResponse.accessToken);
      setLoading(false);
      return loginResponse.accessToken;
    } catch (err) {
      console.error('[Pod] Login failed:', err);
      setError(err.message);
      setLoading(false);
      throw err; // Re-throw so caller can handle
    }
  }, [accessToken]);

  /**
   * Sign out the current user
   * Uses popup for iframe compatibility
   */
  const signOut = useCallback(async () => {
    try {
      const msal = await getMsalInstance();
      const accounts = msal.getAllAccounts();

      if (accounts.length > 0) {
        await msal.logoutPopup({
          account: accounts[0]
        });
      }

      // Clear local state
      setAccessToken(null);
      setAccount(null);
      setError(null);
    } catch (err) {
      console.error('[Pod] Sign out error:', err);
      setError(err.message);
    }
  }, []);

  /**
   * Manually refresh the access token
   */
  const refreshToken = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const msal = await getMsalInstance();
      const accounts = msal.getAllAccounts();

      if (accounts.length === 0) {
        throw new Error('No accounts found');
      }

      const tokenResponse = await msal.acquireTokenSilent({
        ...getTokenRequest(),
        account: accounts[0],
        forceRefresh: true
      });

      console.log('[Pod] Access token refreshed');
      setAccessToken(tokenResponse.accessToken);
      setLoading(false);
      return tokenResponse.accessToken;
    } catch (err) {
      console.error('[Pod] Token refresh error:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, []);

  return {
    // State
    accessToken,
    account,
    loading,
    error,
    isAuthenticated: accessToken !== null,

    // Methods
    login,        // NEW: Explicit login trigger
    signOut,
    refreshToken
  };
}
